import React, { useRef, useEffect } from 'react';
import { User, Briefcase, Home, Building2, Landmark, Car, Wallet } from 'lucide-react';
const solutions = [
  { title: "Personal Loan", rate: "10.50%", icon: User, color: "green" },
  { title: "Business Loan", rate: "10.75%", icon: Briefcase, color: "yellow" },
  { title: "CA / Dr Professional Loan", rate: "10.99%", icon: User, color: "green" },
  { title: "Home Loan", rate: "7.25%", icon: Home, color: "green" },
  { title: "Loan Against Property", rate: "10%", icon: Building2, color: "yellow" },
  { title: "DOD / OD / CC", rate: "9.0%", icon: Landmark, color: "red" },
  { title: "New Car Loan", rate: "7.5%", icon: Car, color: "green" },
  { title: "Old Car Loan", rate: "8%", icon: Car, color: "red" }
];
const colorStyles = {
  green: {
    bg: "bg-gradient-to-br from-green-50 to-emerald-50",
    iconBg: "bg-gradient-to-br from-green-500 to-emerald-600",
    border: "border-green-100",
    text: "text-green-600",
    hover: "hover:shadow-green-100/50",
  },
  yellow: {
    bg: "bg-gradient-to-br from-yellow-50 to-amber-50",
    iconBg: "bg-gradient-to-br from-yellow-500 to-amber-600",
    border: "border-yellow-100",
    text: "text-yellow-600",
    hover: "hover:shadow-yellow-100/50",
  },
  red: {
    bg: "bg-gradient-to-br from-red-50 to-rose-50",
    iconBg: "bg-gradient-to-br from-red-500 to-rose-600",
    border: "border-red-100",
    text: "text-red-600",
    hover: "hover:shadow-red-100/50",
  },
};
const KeyFinancingSolutions = () => {
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const stepWidthRef = useRef(0);
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const firstCard = container.querySelector('.loan-card');
    if (firstCard) {
      const cardWidth = firstCard.offsetWidth;
      const gap = 24; // gap-6 = 1.5rem = 24px
      stepWidthRef.current = cardWidth + gap;
    } else {
      stepWidthRef.current = 280; // fallback: 256px (w-64) + 24px
    }
    const startAutoScroll = () => {
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = setInterval(() => {
        if (!container) return;
        const { scrollLeft, clientWidth, scrollWidth } = container;
        const step = stepWidthRef.current;
        const newScrollLeft = scrollLeft + step;
        if (newScrollLeft + clientWidth >= scrollWidth - 10) {
          container.scrollTo({ left: 0, behavior: 'auto' });
        } else {
          container.scrollBy({ left: step, behavior: 'smooth' });
        }
      }, 3000); 
    };
    const checkAndStart = () => {
      if (container.scrollWidth > container.clientWidth) {
        startAutoScroll();
      }
    };
    checkAndStart();

    window.addEventListener('resize', checkAndStart);
    return () => {
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
      window.removeEventListener('resize', checkAndStart);
    };
  }, []);
  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Key Financing Solutions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our competitive interest rates across a wide range of loan products.
          </p>
        </div>
        <div
          ref={scrollContainerRef}
          className="overflow-x-auto pb-4 scroll-smooth"
          style={{ scrollBehavior: 'smooth' }}
        >
          <div className="flex gap-6 min-w-max">
            {solutions.map((sol, idx) => {
              const style = colorStyles[sol.color];
              return (
                <div
                  key={idx}
                  className={`loan-card group relative rounded-2xl ${style.bg} border ${style.border} shadow-md hover:shadow-xl ${style.hover} transition-all duration-300 hover:-translate-y-1 overflow-hidden w-64 flex-shrink-0`}
                >
                  <div className={`absolute top-0 left-0 w-full h-1 ${style.iconBg}`}></div>
                  <div className="p-5 text-center">
                    <div className="flex justify-center mb-4">
                      <div className={`p-3 rounded-xl ${style.iconBg} shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
                        <sol.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <h3 className="font-bold text-gray-800 text-base mb-1">
                      {sol.title}
                    </h3>
                    <div className={`text-2xl font-extrabold ${style.text} my-2`}>
                      {sol.rate}
                    </div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Interest Rate*</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <p className="text-center text-gray-400 text-xs mt-8">
          *Rates are indicative and subject to change based on lender policies and borrower profile.
        </p>
      </div>
    </section>
  );
};
export default KeyFinancingSolutions;